import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Landmark, Cctv, Sparkles } from "lucide-react";
import { Logo } from "./Logo";

const PRODUCTS = [
  { to: "/products/laic", label: "LAIC", sub: "LGU AI Companion — e-governance", icon: Landmark, testId: "nav-product-laic" },
  { to: "/products/vision", label: "MAC AI Vision", sub: "Camera intelligence — cities & campuses", icon: Cctv, testId: "nav-product-vision" },
  { to: "/contact", label: "More Coming", sub: "In development — ask us what's next", icon: Sparkles, testId: "nav-product-more" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!window.__lenis) return;
    if (open) window.__lenis.stop();
    else window.__lenis.start();
  }, [open]);

  useEffect(() => {
    setOpen(false);
    setProductsOpen(false);
  }, [pathname]);

  const linkCls = (to) =>
    `font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${
      pathname === to ? "text-mac-accent" : "text-slate-400 hover:text-mac-accent"
    }`;

  const productsActive = pathname.startsWith("/products");

  return (
    <header
      data-testid="site-nav"
      className={`fixed inset-x-0 top-0 z-50 border-b transition-[background-color,border-color] duration-500 ${
        scrolled ? "border-white/10 bg-mac-base/85 backdrop-blur-xl" : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between px-6 lg:px-8">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.25, duration: 0.45 }}>
          <Link to="/" aria-label="MAC AI home" data-testid="nav-logo-button">
            <Logo />
          </Link>
        </motion.div>

        <nav className="hidden items-center gap-7 lg:flex">
          <div className="relative">
            <button
              data-testid="nav-products-dropdown"
              onClick={() => setProductsOpen(!productsOpen)}
              className={`flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-[0.18em] transition-colors duration-300 ${
                productsActive ? "text-mac-accent" : "text-slate-400 hover:text-mac-accent"
              }`}
            >
              Products <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-300 ${productsOpen ? "rotate-180" : ""}`} />
            </button>
            <AnimatePresence>
              {productsOpen && (
                <motion.div
                  data-testid="nav-products-menu"
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={{ duration: 0.2 }}
                  className="absolute left-0 top-full mt-4 w-80 border border-white/10 bg-mac-surface/95 backdrop-blur-xl"
                >
                  {PRODUCTS.map((p) => (
                    <Link
                      key={p.testId}
                      to={p.to}
                      data-testid={p.testId}
                      className="flex items-start gap-3.5 border-b border-white/5 px-5 py-4 transition-colors duration-300 last:border-0 hover:bg-mac-accent/10"
                    >
                      <p.icon className="mt-0.5 h-4 w-4 shrink-0 text-mac-accent" strokeWidth={1.5} />
                      <span>
                        <span className="block font-display text-base font-semibold uppercase tracking-wider text-white">{p.label}</span>
                        <span className="mt-0.5 block font-mono text-[10px] uppercase tracking-[0.12em] text-slate-500">{p.sub}</span>
                      </span>
                    </Link>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
          <Link to="/about" className={linkCls("/about")} data-testid="nav-about">Who We Are</Link>
          <Link to="/contact" className={linkCls("/contact")} data-testid="nav-contact">Contact</Link>
          <Link to="/contact" data-testid="nav-demo-button" className="btn-primary px-5 py-2.5 text-xs">
            Request a Demo
          </Link>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="grid h-10 w-10 place-items-center border border-white/15 text-white lg:hidden"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="overflow-hidden border-t border-white/10 bg-mac-base/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              <p className="py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-slate-600">Products</p>
              {PRODUCTS.map((p) => (
                <Link
                  key={p.testId}
                  to={p.to}
                  data-testid={`${p.testId}-mobile`}
                  className="border-b border-white/5 py-3 pl-4 font-display text-lg font-semibold uppercase tracking-widest text-slate-300 transition-colors duration-300 hover:text-mac-accent"
                >
                  {p.label}
                  <span className="ml-2 font-mono text-[10px] font-normal normal-case tracking-[0.1em] text-slate-500">{p.sub}</span>
                </Link>
              ))}
              <Link to="/about" data-testid="nav-about-mobile" className="border-b border-white/5 py-3 font-display text-lg font-semibold uppercase tracking-widest text-slate-300 transition-colors duration-300 hover:text-mac-accent">
                Who We Are
              </Link>
              <Link to="/contact" data-testid="nav-contact-mobile" className="border-b border-white/5 py-3 font-display text-lg font-semibold uppercase tracking-widest text-slate-300 transition-colors duration-300 hover:text-mac-accent">
                Contact
              </Link>
              <Link to="/contact" data-testid="nav-demo-button-mobile" className="btn-primary mt-4 justify-center">
                Request a Demo
              </Link>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
